import { AppointmentDTO } from '@om1/aspen-api/codegen/client/models/AppointmentDTO'

import { OPERATIONS_ZONE } from './timezone'

/**
 * Rendering helpers for a candidate's Cal.com appointment (APP-1220).
 *
 * ## Why these take a timezone at all
 *
 * Everything in aspen previously formatted times with bare
 * `toLocaleString()` — which renders in the **browser's** zone. For an
 * Eastern-based coordinator that looks indistinguishable from "we display
 * Eastern", so the bug was invisible: a Pacific patient's 9am appointment
 * showed as 12pm, and nothing about the screen suggested anything was wrong.
 *
 * So these format in the **patient's** zone explicitly and label it. A
 * coordinator ringing a patient needs to know what time it is *for the
 * patient*, and the label is what makes the number actionable rather than
 * ambiguous.
 */

/** Fallback when a candidate predates `patient_candidate.timezone` (APP-1210). */
const DEFAULT_ZONE = 'America/New_York'

export const appointmentZone = (appointment: Pick<AppointmentDTO, 'timezone'>, candidateTimezone?: string | null): string =>
    appointment.timezone ?? candidateTimezone ?? DEFAULT_ZONE

/**
 * Whether we actually know which zone to read this appointment in.
 *
 * `patient_candidate.timezone` is net-new (APP-1210), so every candidate who
 * registered before it is NULL — which is all of the `legacy_preference` rows
 * the APP-1222 backfill creates. For those we still have to pick *some* zone to
 * put a clock time on screen, and Eastern is the least-wrong choice because the
 * study team sits there. But rendering `9:00 AM EDT` asserts knowledge we do not
 * have, and a coordinator reading that has no way to tell it from a real
 * Pacific-patient booking. That is the exact confusion APP-1220 removed.
 *
 * So the zone is still Eastern, and the label says so honestly instead.
 */
export const appointmentZoneIsKnown = (appointment: Pick<AppointmentDTO, 'timezone'>, candidateTimezone?: string | null): boolean =>
    Boolean(appointment.timezone ?? candidateTimezone)

/**
 * Human-readable status. Kept as an explicit map rather than prettifying the
 * raw value, so a new status added backend-side surfaces as an obvious gap
 * instead of silently rendering `legacy_preference` at a coordinator.
 */
export const APPOINTMENT_STATUS_LABELS: Record<AppointmentDTO['status'], string> = {
    held: 'Hold',
    confirmed: 'Confirmed',
    completed: 'Completed',
    no_show: 'No-show',
    cancelled: 'Cancelled',
    legacy_preference: 'Preference only'
}

/**
 * antd Tag colours. `legacy_preference` is deliberately grey rather than a
 * warning colour: it is not a problem, it is a candidate who registered before
 * the scheduler existed.
 */
export const APPOINTMENT_STATUS_COLORS: Record<AppointmentDTO['status'], string> = {
    held: 'gold',
    confirmed: 'green',
    completed: 'blue',
    no_show: 'red',
    cancelled: 'default',
    legacy_preference: 'default'
}

/**
 * Statuses where a coordinator can still act on the booking upstream.
 *
 * The explicit `new Set<...>` type argument is load-bearing, not noise: the
 * monorepo typechecks with TS 5.1, which infers it from the annotation, but
 * aspen *builds* with react-scripts on TS 4.9, which does not — it infers
 * `Set<string>` and rejects the assignment. Written this way both agree.
 */
export const ACTIONABLE_APPOINTMENT_STATUSES: ReadonlySet<AppointmentDTO['status']> = new Set<AppointmentDTO['status']>(['held', 'confirmed'])

/**
 * Whether coordinator actions make sense for this appointment.
 *
 * A cancelled or completed booking cannot be cancelled again, and Cal.com
 * refuses no-show on something that has not happened — so offering the buttons
 * would invite a 409 the coordinator can do nothing about. A row with no
 * `calcomBookingUid` is the `legacy_preference` case: there is nothing upstream
 * to act on, and the API refuses it with a 422.
 */
export const canActOnAppointment = (appointment?: AppointmentDTO | null): boolean =>
    Boolean(appointment && appointment.calcomBookingUid && ACTIONABLE_APPOINTMENT_STATUSES.has(appointment.status))

const safeFormat = (iso: string, zone: string, options: Intl.DateTimeFormatOptions): string | null => {
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return null
    try {
        return date.toLocaleString('en-US', { timeZone: zone, ...options })
    } catch {
        // An unknown IANA id would throw and take the whole table cell with it.
        return date.toLocaleString('en-US', { timeZone: DEFAULT_ZONE, ...options })
    }
}

/**
 * `Wed, Sep 3 · 12:00 PM–12:30 PM EDT (9:00 AM PDT)` — one line, Eastern first.
 *
 * Eastern leads because the operations team reads and filters the list in Eastern
 * (APP-1720); with each row in its own zone, scanning a day's bookings meant
 * mentally converting every line, and a date filter could not agree with any of
 * them. The day shown is therefore the Eastern day, which is the day the filter
 * matches on.
 *
 * The patient's local time is kept alongside rather than dropped. A coordinator
 * ringing a patient needs to know what time it is *for the patient* — that is why
 * APP-1220 moved off browser-local rendering in the first place — so removing it
 * to satisfy "display in Eastern" would trade one confusion for another.
 *
 * The parenthetical is omitted when the patient shares Eastern's wall clock, since
 * repeating the same time twice is noise, and when the patient's zone is unknown,
 * where it is replaced by an explicit note rather than an assumed time.
 */
export const formatAppointmentSlot = (appointment: AppointmentDTO, candidateTimezone?: string | null): string | null => {
    const patientZone = appointmentZone(appointment, candidateTimezone)
    const known = appointmentZoneIsKnown(appointment, candidateTimezone)

    const day = safeFormat(appointment.startAt, OPERATIONS_ZONE, { weekday: 'short', month: 'short', day: 'numeric' })
    const start = safeFormat(appointment.startAt, OPERATIONS_ZONE, { hour: 'numeric', minute: '2-digit', hour12: true })
    const end = safeFormat(appointment.endAt, OPERATIONS_ZONE, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short' as const
    })
    if (!day || !start || !end) return null

    const slot = `${day} · ${start}–${end}`

    if (!known) return `${slot} (patient timezone unknown)`

    const clock = { hour: 'numeric' as const, minute: '2-digit' as const, hour12: true, timeZoneName: 'short' as const }
    const patientStart = safeFormat(appointment.startAt, patientZone, clock)
    const easternStart = safeFormat(appointment.startAt, OPERATIONS_ZONE, clock)

    // Compared as rendered rather than by offset, which also covers the case where
    // an unrecognised IANA id made safeFormat fall back to Eastern: the bracket
    // would otherwise repeat the Eastern time back at the coordinator.
    if (!patientStart || patientStart === easternStart) return slot
    return `${slot} (${patientStart})`
}
